import React, { useState, useEffect } from 'react';
import { Card, Tabs, Input, Button, Table, message, Image, Spin } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const { TabPane } = Tabs;

function BinomialDist({ fileId, fileName, setSelectedFile, initialConfig }) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [inputData, setInputData] = useState({
    number_of_trials: initialConfig?.number_of_trials ?? 0,
    specific_number_r: initialConfig?.specific_number_r ?? 0,
    probability_of_success: initialConfig?.probability_of_success ?? 0
  });
  const [results, setResults] = useState(null);
  const [chartUrl, setChartUrl] = useState("");

  useEffect(() => {
    if (fileId) {
      retrieveAnalysis();
    }
  }, [fileId]);

  const retrieveAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`/api/retrieve_Binomial_dist/${fileId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setInputData(response.data.input_data || {});
        setResults(response.data.output_data?.results || null);
        setChartUrl(response.data.chart_url || "");
        if (response.data.output_data) {
          setActiveTab("2");
        }
      }
    } catch (error) {
      console.error("Error retrieving analysis:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to retrieve analysis.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSolve = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        name: fileName || "Untitled Analysis",
        input_data: {
          number_of_trials: Number(inputData.number_of_trials),
          specific_number_r: Number(inputData.specific_number_r),
          probability_of_success: Number(inputData.probability_of_success)
        }
      };

      let response;
      if (fileId) {
        response = await axios.put(`/api/update_Binomial_dist/${fileId}/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`/api/save_Binomial_dist/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data) {
        message.success("Analysis saved successfully!");
        setResults(response.data.output_data?.results || null);
        setChartUrl(response.data.chart_url || "");

        // Update selectedFile to prevent duplicate saves
        if (!fileId && response.data.id) {
          setSelectedFile(prev => ({
            ...prev,
            id: response.data.id,
            name: response.data.name
          }));
        }

        setActiveTab("2");
      }
    } catch (error) {
      console.error("Error saving analysis:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error(error.response?.data?.error || "Failed to save analysis.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOutputTable = () => {
    if (!results || !results.distribution_table) return null;

    const columns = [
      {
        title: 'Number of Successes (r)',
        dataIndex: 'r',
        key: 'r',
        align: 'center'
      },
      {
        title: 'Probability (P(r))',
        dataIndex: 'P_r',
        key: 'P_r',
        align: 'center'
      },
      {
        title: 'Cumulative Probability',
        dataIndex: 'cumulative_probability',
        key: 'cumulative_probability',
        align: 'center'
      }
    ];

    const dataSource = results.distribution_table.map((row, index) => ({
      key: index,
      ...row
    }));

    return (
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
        size="small"
      />
    );
  };

  return (
    <Card
      title={`Binomial Distribution: ${fileName || 'Untitled Analysis'}`}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSolve}
          loading={loading}
        >
          Solve & Save
        </Button>
      }
    >
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Inputs" key="1">
            <div style={{ maxWidth: 400, margin: '0 auto' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8 }}>Number of trials (n):</label>
                <Input
                  type="number"
                  name="number_of_trials"
                  value={inputData.number_of_trials}
                  onChange={handleInputChange}
                  min={0}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8 }}>Probability of success (p):</label>
                <Input
                  type="number"
                  name="probability_of_success"
                  value={inputData.probability_of_success}
                  onChange={handleInputChange}
                  step={0.01}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8 }}>Specific number (r):</label>
                <Input
                  type="number"
                  name="specific_number_r"
                  value={inputData.specific_number_r}
                  onChange={handleInputChange}
                  min={0}
                />
              </div>
            </div>
          </TabPane>
          <TabPane tab="Results" key="2" disabled={!results}>
            {results && (
              <div style={{ marginTop: 24 }}>
                <h3 style={{ textAlign: 'center', marginBottom: 16 }}>Distribution Table</h3>
                {renderOutputTable()}

                {chartUrl && (
                  <div style={{ textAlign: 'center', marginTop: 32 }}>
                    <h3>Chart</h3>
                    <Image
                      src={chartUrl}
                      alt="Binomial Distribution Chart"
                      fallback="https://via.placeholder.com/600x400?text=Chart+Not+Available"
                      style={{ maxWidth: '100%', border: '1px solid #d9d9d9', borderRadius: 8 }}
                    />
                  </div>
                )}
              </div>
            )}
          </TabPane>
        </Tabs>
      </Spin>
    </Card>
  );
}

export default BinomialDist;
